import { HubspotListDialog } from "@/components/hubspot-list-dialog";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { EmployeeDTO, HubSpotListDTO, HubSpotSaveToListResultDTO } from "@shaarp/common";
import { useState } from "react";
import { toast, useToastError } from "../ui/use-toast";

type EmployeesHubspotListExportProps = {
  employees: EmployeeDTO[];
  companyId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEmployeesSynced?: (syncedEmployeeIds: { id: string; externalId: string; directLink: string | null }[]) => void;
};

export function EmployeesHubspotListExport({
  employees,
  companyId,
  open,
  onOpenChange,
  onEmployeesSynced,
}: EmployeesHubspotListExportProps) {
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (selectedList: HubSpotListDTO) => {
    const employeesIds = employees.map((e) => e.id);
    setIsSaving(true);
    try {
      const { data: result } = await api.post<HubSpotSaveToListResultDTO>(
        `/integration/crm/hubspot/contact-lists/${selectedList.listId}/records`,
        { employeesIds, listName: selectedList.name, ...(companyId && { companyId }) }
      );
      onOpenChange(false);
      onEmployeesSynced?.(result.syncedRecords);
      toast({
        variant: "default",
        title: t("components.dataTableCompanyEmployees.hubspotList.successTitle"),
        description:
          result.failedCount > 0
            ? t(
                "components.dataTableCompanyEmployees.hubspotList.successDescriptionWithFailures",
                {
                  success: result.successCount,
                  listName: result.listName,
                  failed: result.failedCount,
                }
              )
            : t(
                "components.dataTableCompanyEmployees.hubspotList.successDescription",
                {
                  success: result.successCount,
                  listName: result.listName,
                }
              ),
      });
    } catch {
      toastError();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <HubspotListDialog
      type="contact"
      open={open}
      onOpenChange={onOpenChange}
      count={employees.length}
      isSaving={isSaving}
      onSave={handleSave}
      translationPrefix="components.dataTableCompanyEmployees.hubspotList"
    />
  );
}
